import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import {
    VStack,
    HStack,
    Text,
    Button,
    Box,
    Select,
    Input,
    Badge,
} from '@chakra-ui/react';
import { useAppStore } from '../../state/store';
import { useWorksharingStore, getInterferenceModelOptions } from '../../state/useWorksharingStore';
import type { InterferenceReport } from '../../state/useWorksharingStore';
import { ManageDialogFrame, ManageDialogPrimaryButton, ManageDialogCloseFooter } from '../manage/ManageDialogFrame';
import { notify } from '../../utils/notify';

/**
 * Interference Check dialog (Collaborate > Coordinate > Interference Check).
 * BIM's "Interference Check: Run / Show Report" workflow: pick the two
 * models (current vs linked), set a tolerance, run the check, and inspect
 * the resulting clash rows.
 */
export function InterferenceCheckDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.collabDialog === 'interference');
    const onClose = useAppStore((state) => state.closeCollabDialog);
    const prefillReport = useAppStore((state) => state.collabDialogPayload === 'lastReport');
    const runInterferenceCheck = useWorksharingStore((s) => s.runInterferenceCheck);
    const lastReport = useWorksharingStore((s) =>s.lastInterferenceReport); const options = getInterferenceModelOptions(); const [modelA, setModelA] = useState('Current Project'); const [modelB, setModelB] = useState(options[1] ?? 'Current Project'); const [tolerance, setTolerance] = useState('0'); const [report, setReport] = useState<InterferenceReport | null>(
        prefillReport ? lastReport : null
    );
    const [running, setRunning] = useState(false);

    const handleRun = () => {
        if (modelA === modelB) {
            notify('Select two different models to compare.', 'warning');
            return;
        }
        setRunning(true);
        // Defer so the button state paints before the (synchronous) clash sweep.
        window.setTimeout(() => {
            const tol = Math.max(0, Number(tolerance) || 0);
            setReport(runInterferenceCheck(modelA, modelB, tol));
            setRunning(false);
        }, 50);
    };

    return (
        <ManageDialogFrame
            title={t('collab.interferenceCheck')}
            isOpen={isOpen}
            onClose={onClose}
            size="xl"
            footer={
                <HStack spacing={2} w="full" justify="space-between">
                    <HStack spacing={2}>
                        <ManageDialogPrimaryButton
                            label={running ? 'Checking...' : 'Run Interference Check'}
                            onClick={handleRun}
                            isDisabled={running}
                        />
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                                if (lastReport) setReport(lastReport);
                                else notify('No interference check has been run yet in this session.', 'info');
                            }}
                        >{t('collab.showLastReport')}</Button>
                    </HStack>
                    <ManageDialogCloseFooter onClose={onClose} />
                </HStack>
            }
        >
            <VStack spacing={4} align="stretch">
                <HStack spacing={3} align="flex-end" flexWrap="wrap">
                    <VStack align="start" spacing={1}>
                        <Text fontSize="xs" fontWeight="semibold">{t('collab.modelA')}</Text>
                        <Select
                            size="sm"
                            value={modelA}
                            onChange={(e) => setModelA(e.target.value)}
                            bg="var(--color-bg-primary)"
                            borderColor="var(--color-border)"
                            minW="200px"
                        >
                            {options.map((o) => <option key={o} value={o}>{o}</option>)}
                        </Select>
                    </VStack>
                    <VStack align="start" spacing={1}>
                        <Text fontSize="xs" fontWeight="semibold">{t('collab.modelB')}</Text>
                        <Select
                            size="sm"
                            value={modelB}
                            onChange={(e) => setModelB(e.target.value)}
                            bg="var(--color-bg-primary)"
                            borderColor="var(--color-border)"
                            minW="200px"
                        >
                            {options.map((o) => <option key={o} value={o}>{o}</option>)}
                        </Select>
                    </VStack>
                    <VStack align="start" spacing={1}>
                        <Text fontSize="xs" fontWeight="semibold">{t('collab.toleranceM')}</Text>
                        <Input
                            size="sm"
                            type="number"
                            min="0"
                            step="0.01"
                            value={tolerance}
                            onChange={(e) => setTolerance(e.target.value)}
                            bg="var(--color-bg-primary)"
                            borderColor="var(--color-border)"
                            w="100px"
                        />
                    </VStack>
                </HStack>

                {report ? (
                    <VStack spacing={2} align="stretch">
                        <HStack>
                            <Text fontSize="sm" fontWeight="semibold">
                                {report.total} interference(s)
                            </Text>
                            <Text fontSize="xs" color="var(--color-text-secondary)">
                                {report.modelA} vs {report.modelB} · tolerance {report.tolerance} m ·{' '}
                                {new Date(report.runAt).toLocaleTimeString()}
                            </Text>
                        </HStack>
                        <Box
                            borderWidth="1px"
                            borderColor="var(--color-border)"
                            borderRadius="var(--cad-radius-sm)"
                            maxH="320px"
                            overflowY="auto"
                        >
                            <HStack
                                px={3}
                                py={1.5}
                                bg="var(--color-bg-tertiary)"
                                borderBottom="1px solid var(--color-border)"
                                spacing={3}
                                fontSize="xs"
                                fontWeight="semibold"
                                color="var(--color-text-secondary)"
                            >
                                <Box w="80px" flexShrink={0}>{t('collab.type')}</Box>
                                <Box flex="1 1 auto" minW={0}>{t('collab.elementA')}</Box>
                                <Box flex="1 1 auto" minW={0}>{t('collab.elementB')}</Box>
                                <Box w="90px" flexShrink={0} textAlign="right">{t('collab.overlapM')}</Box>
                            </HStack>
                            {report.rows.length === 0 ? (
                                <Text fontSize="sm" color="var(--color-text-secondary)" p={4} textAlign="center">{t('collab.noInterferencesFoundBetweenTheSelectedModels')}</Text>
                            ) : (
                                report.rows.map((row) => (
                                    <HStack key={row.id} px={3} py={1.5} spacing={3} borderBottom="1px solid var(--color-border)">
                                        <Box w="80px" flexShrink={0}>
                                            <Badge colorScheme={row.type === 'Hard' ? 'red' : 'yellow'} variant="subtle">{row.type}</Badge>
                                        </Box>
                                        <Box flex="1 1 auto" minW={0}>
                                            <Text fontSize="sm" isTruncated>{row.typeA}</Text>
                                            <Text fontSize="xs" color="var(--color-text-secondary)" isTruncated>{row.categoryA}</Text>
                                        </Box>
                                        <Box flex="1 1 auto" minW={0}>
                                            <Text fontSize="sm" isTruncated>{row.typeB}</Text>
                                            <Text fontSize="xs" color="var(--color-text-secondary)" isTruncated>{row.categoryB}</Text>
                                        </Box>
                                        <Box w="90px" flexShrink={0} textAlign="right" fontSize="xs">
                                            {row.distance.toFixed(3)}
                                        </Box>
                                    </HStack>
                                ))
                            )}
                        </Box>
                    </VStack>
                ) : (
                    <Box p={6} textAlign="center" borderWidth="1px" borderColor="var(--color-border)" borderRadius="var(--cad-radius-sm)">
                        <Text fontSize="sm" color="var(--color-text-secondary)">{t('collab.runACheckBetweenTheCurrentProjectAndALinkedModelToListInterferingE')}</Text>
                    </Box>
                )}
            </VStack>
        </ManageDialogFrame>
    );
}
